import {
  collection,
  CollectionReference,
  doc,
  DocumentReference,
  Firestore,
  query,
  where,
} from 'firebase/firestore';
import isEqual from 'lodash/isEqual';
import { collectionData, docData } from 'rxfire/firestore';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { ICrossTestMetricSelection, IUser } from '../../../types';
import { authenticatedUserProvider } from '../../userProvider';
import { IComparisonPopulationSetting } from '../comparisonPopulationSettingsProvider';
import { IDateRange } from '../dates/dateRangeProvider';
import { ISettingsTemplate } from './types';

export interface ITsaReportSettings {
  metrics: ICrossTestMetricSelection[];
}

/**
 * @description the subset of the TSA settings that are not template specific
 */
interface ITsaReportSettingOptions {
  targetTimeframe: IDateRange;
  comparisonPopulation: IComparisonPopulationSetting;
}

/**
 * @description the complete document in the database
 */
interface ITsaReportSettingEntity extends ITsaReportSettingOptions {
  selected: ISettingsTemplate<ITsaReportSettings>;
}

/**
 * @description the interface that is emitted by the provider
 */
export interface ITsaReportSettingsState extends ITsaReportSettingOptions {
  selected: ISettingsTemplate<ITsaReportSettings>;
  templates: Array<ISettingsTemplate<ITsaReportSettings>>;
}

const defaultSettings: ITsaReportSettings = {
  metrics: [],
};

/**
 * @description observes the TSA report settings and related settings templates
 */
export const tsaReportSettingsProvider =
  (): Observable<ITsaReportSettingsState> => {
    return authenticatedUserProvider().pipe(
      switchMap(
        (
          user: IUser
        ): Observable<
          [
            ISettingsTemplate<ITsaReportSettings>,
            ITsaReportSettingOptions,
            Array<ISettingsTemplate<ITsaReportSettings>>
          ]
        > => {
          return combineLatest([
            selectedTemplateProvider({
              userId: user.id,
              firestore: user.services.firestore,
            }),
            tsaSettingsOptionsProvider({
              userId: user.id,
              firestore: user.services.firestore,
            }),
            templatesProvider({
              userId: user.id,
              firestore: user.services.firestore,
            }),
          ]);
        }
      ),
      map(
        (
          result: [
            ISettingsTemplate<ITsaReportSettings>,
            ITsaReportSettingOptions,
            Array<ISettingsTemplate<ITsaReportSettings>>
          ]
        ): ITsaReportSettingsState => {
          const [selected, options, templates] = result;
          if (selected?.id) {
            return {
              selected,
              templates,
              ...options,
            };
          } else {
            return {
              selected: null,
              templates,
              ...options,
            };
          }
        }
      )
    );
  };

interface ISelectedTemplateProviderArgs {
  userId: string;
  firestore: Firestore;
}
/**
 * @description observes the selected template
 * @param userId
 * @returns
 */
const selectedTemplateProvider = (
  args: ISelectedTemplateProviderArgs
): Observable<ISettingsTemplate<ITsaReportSettings>> => {
  const { userId, firestore } = args;
  return docData(
    doc(
      firestore,
      `userProfiles/${userId}/settings/tsaReport`
    ) as DocumentReference<ITsaReportSettingEntity>
  ).pipe(
    map(
      (
        entity: ITsaReportSettingEntity
      ): ISettingsTemplate<ITsaReportSettings> => entity?.selected
    ),
    distinctUntilChanged(
      (
        prev: ISettingsTemplate<ITsaReportSettings>,
        curr: ISettingsTemplate<ITsaReportSettings>
      ): boolean => isEqual(prev, curr)
    )
  );
};

interface ITsaSettingsOptionsProviderArgs {
  userId: string;
  firestore: Firestore;
}

/**
 * @description observes changes the subset of the TSA settings that are not template specific
 * @param userId
 * @returns
 */
const tsaSettingsOptionsProvider = (
  args: ITsaSettingsOptionsProviderArgs
): Observable<ITsaReportSettingOptions> => {
  const { userId, firestore } = args;
  return docData(
    doc(
      firestore,
      `userProfiles/${userId}/settings/tsaReport`
    ) as DocumentReference<ITsaReportSettingEntity>
  ).pipe(
    map(
      (entity: ITsaReportSettingEntity): ITsaReportSettingOptions => ({
        targetTimeframe: entity?.targetTimeframe,
        comparisonPopulation: entity?.comparisonPopulation,
      })
    ),
    distinctUntilChanged(
      (
        prev: ITsaReportSettingOptions,
        curr: ITsaReportSettingOptions
      ): boolean => isEqual(prev, curr)
    )
  );
};

interface ITemplatesProviderArgs {
  userId: string;
  firestore: Firestore;
}
/**
 * @description observes the settings templates the user has saved
 * @param userId
 * @returns
 */
const templatesProvider = (
  args: ITemplatesProviderArgs
): Observable<Array<ISettingsTemplate<ITsaReportSettings>>> => {
  const { userId, firestore } = args;
  return collectionData(
    query(
      collection(
        firestore,
        `userProfiles/${userId}/settings/tsaReport/templates`
      ) as CollectionReference<ISettingsTemplate<ITsaReportSettings>>,
      where('active', '==', true)
    )
  );
};
